"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import { useUserContext } from '@/app/_module/store/contexts/userContext/userContexts';
import { usePathname, useRouter } from 'next/navigation';

// Lazy load socket.io and rrweb only when co-browse session is started
// This saves ~200-300KB from initial bundle
let socketIOModule: typeof import('socket.io-client') | null = null;
let rrwebModule: typeof import('rrweb') | null = null;

// InsuraBot highlight keyframes — injected once on first use
let keyframesInjected = false;
const ensureHighlightKeyframes = () => {
  if (keyframesInjected || typeof document === 'undefined') return;
  if (document.getElementById('insurabot-highlight-style')) {
    keyframesInjected = true;
    return;
  }
  const style = document.createElement('style');
  style.id = 'insurabot-highlight-style';
  style.textContent = `
@keyframes insurabot-highlight-pulse {
  0%   { transform: scale(1);    opacity: 1; }
  50%  { transform: scale(1.04); opacity: 0.85; }
  100% { transform: scale(1);    opacity: 1; }
}
`;
  document.head.appendChild(style);
  keyframesInjected = true;
};

// Render a pulsing bounding-box ring around the given element.
// Used by both `highlight` and `click` command handlers for visual confirmation.
const renderInsuraBotHighlight = (el: HTMLElement) => {
  if (!el || typeof document === 'undefined') return;
  ensureHighlightKeyframes();

  el.scrollIntoView({ behavior: 'smooth', block: 'center' });

  const rect = el.getBoundingClientRect();
  // Skip highlight for hidden / zero-sized / offscreen-negative elements —
  // otherwise we'd render an invisible or meaningless ring.
  const MIN_DIM = 4;
  if (rect.width < MIN_DIM || rect.height < MIN_DIM) return;
  const overlay = document.createElement('div');
  overlay.className = 'insurabot-highlight-ring';
  overlay.style.cssText = `
    position: fixed;
    left: ${rect.left - 6}px;
    top: ${rect.top - 6}px;
    width: ${rect.width + 12}px;
    height: ${rect.height + 12}px;
    border: 3px solid #3b82f6;
    border-radius: 10px;
    box-shadow: 0 0 0 3px rgba(59,130,246,0.25),
                0 0 24px rgba(59,130,246,0.5);
    pointer-events: none;
    z-index: 999998;
    animation: insurabot-highlight-pulse 1.2s ease-out 2;
  `;
  document.body.appendChild(overlay);
  setTimeout(() => overlay.remove(), 2400);
};

const loadDependencies = async () => {
  if (!socketIOModule || !rrwebModule) {
    const [socketIO, rrweb] = await Promise.all([
      import('socket.io-client'),
      import('rrweb'),
    ]);
    socketIOModule = socketIO;
    rrwebModule = rrweb;
  }
  return { io: socketIOModule.io, rrweb: rrwebModule };
};

export interface CoBrowseSession {
  id: string;
  status: 'inactive' | 'waiting' | 'active';
  adminName?: string;
  remoteControlEnabled: boolean;
}

export interface RemoteCommand {
  type: 'click' | 'scroll' | 'highlight' | 'navigate' | 'focus';
  payload: any;
}

export interface ChatMessage {
  id: string;
  sessionId: string;
  senderId: string;
  senderName: string;
  senderType: 'user' | 'admin';
  content: string;
  timestamp: Date;
}

export interface CoBrowseContextType {
  session: CoBrowseSession | null;
  isRecording: boolean;
  startSession: () => Promise<void>;
  endSession: () => void;
  grantRemoteControl: (granted: boolean) => void;
  adminCursor: { x: number; y: number; name: string; color: string } | null;
  controlRequest: boolean;
  respondToControlRequest: (granted: boolean) => void;
  // Chat functionality
  chatMessages: ChatMessage[];
  sendChatMessage: (content: string) => void;
  isAdminTyping: boolean;
  setTyping: (isTyping: boolean) => void;
  // AI Agent Mode
  isAiAdmin: boolean;
  aiAssistActive: boolean;
  requestAiAssist: (userMessage?: string) => Promise<void>;
  stopAiAssist: () => Promise<void>;
  sendAiAssistMessage: (message: string) => Promise<void>;
}

const CoBrowseContext = createContext<CoBrowseContextType | null>(null);

export const useCoBrowse = () => {
  const context = useContext(CoBrowseContext);
  if (!context) {
    throw new Error('useCoBrowse must be used within CoBrowseProvider');
  }
  return context;
};

interface CoBrowseProviderProps {
  children: React.ReactNode;
}

export const CoBrowseProvider: React.FC<CoBrowseProviderProps> = ({ children }) => {
  const { user } = useUserContext();
  const pathname = usePathname();
  const router = useRouter();

  const [session, setSession] = useState<CoBrowseSession | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [adminCursor, setAdminCursor] = useState<{ x: number; y: number; name: string; color: string } | null>(null);
  const [aiCursorVisible, setAiCursorVisible] = useState(false);
  const [aiCursorPos, setAiCursorPos] = useState<{ x: number; y: number }>({ x: -100, y: -100 }); // Start off-screen
  const [controlRequest, setControlRequest] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isAdminTyping, setIsAdminTyping] = useState(false);
  const [isAiAdmin, setIsAiAdmin] = useState(false);
  const [aiAssistActive, setAiAssistActive] = useState(false);
  const aiStateRef = useRef<'idle' | 'starting' | 'active' | 'stopping'>('idle');

  const socketRef = useRef<Socket | null>(null);
  const lastResolvedElementRef = useRef<HTMLElement | null>(null);
  const lastResolveQueryRef = useRef<{ selector?: string; textContent?: string } | null>(null);
  const stopRecordingRef = useRef<(() => void) | null>(null);
  const eventBufferRef = useRef<any[]>([]);
  const flushIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const sessionIdRef = useRef<string | null>(null); // Keep session ID in ref for recording callback
  const forceFullSnapshotRef = useRef<(() => void) | null>(null); // Ref to forceFullSnapshot for socket handlers
  const pendingSnapshotRef = useRef<any[] | null>(null); // Track pending snapshot to retry on reconnect

  // Initialize WebSocket connection - lazy loaded
  const initSocket = useCallback(async () => {
    if (socketRef.current?.connected) return socketRef.current;

    // Lazy load socket.io
    const { io } = await loadDependencies();

    const socket = io(`${process.env.NEXT_PUBLIC_API_BASE_URL}/cobrowse`, {
      transports: ['websocket'],
      autoConnect: true,
    });

    // Wait for the socket to actually connect before returning. Without this
    // we'd emit `session:start` against a still-connecting socket — the emit
    // gets buffered and the ack callback never fires (infinite "AI Assistant
    // loading" was caused by this on cold-start dev backends). 30s timeout
    // covers Render free-tier hibernation wake-up + handshake.
    if (!socket.connected) {
      await new Promise<void>((resolve, reject) => {
        const timer = setTimeout(() => {
          socket.off('connect', onConnect);
          socket.off('connect_error', onError);
          reject(new Error('Could not reach the assistant server. Please try again.'));
        }, 30000);
        const onConnect = () => {
          clearTimeout(timer);
          socket.off('connect_error', onError);
          resolve();
        };
        const onError = (err: any) => {
          clearTimeout(timer);
          socket.off('connect', onConnect);
          reject(new Error(err?.message || 'Could not connect to the assistant server.'));
        };
        socket.once('connect', onConnect);
        socket.once('connect_error', onError);
      });
    }

    socket.on('connect', () => {
      // Connected to server
    });

    socket.on('disconnect', () => {
      // Don't clear session on disconnect - let the server handle reconnection
      // The session will be cleared if the server ends it or if reconnection fails
      setIsRecording(false);
    });

    // Admin joined the session — use server-side isAiAdmin flag (can't be spoofed)
    socket.on('admin:joined', (data: { sessionId: string; adminName: string; isAiAdmin?: boolean }) => {
      const isAi = data.isAiAdmin === true;
      setIsAiAdmin(isAi);
      setSession(prev => prev ? {
        ...prev,
        status: 'active',
        adminName: isAi ? 'InsuraBot AI' : 'Support Agent',
        remoteControlEnabled: isAi ? true : prev.remoteControlEnabled, // Auto-grant for AI
      } : null);

      // Auto-grant remote control for AI Agent Mode — user already consented by clicking Start
      if (isAi) {
        socket.emit('control:response', {
          sessionId: data.sessionId,
          granted: true,
        });
      }
    });

    // Admin disconnected
    socket.on('admin:disconnected', () => {
      setSession(prev => prev ? {
        ...prev,
        status: 'waiting',
        adminName: undefined,
        remoteControlEnabled: false,
      } : null);
      setAdminCursor(null);
    });

    // Admin requests remote control
    socket.on('control:request', (data: { adminName: string }) => {
      setControlRequest(true);
    });

    // Admin cursor position — validate inputs
    let cursorHideTimer: NodeJS.Timeout | null = null;
    socket.on('cursor:admin', (data: { x: number; y: number; color: string; name: string }) => {
      const x = typeof data.x === 'number' && isFinite(data.x) ? Math.max(0, Math.min(data.x, 10000)) : 0;
      const y = typeof data.y === 'number' && isFinite(data.y) ? Math.max(0, Math.min(data.y, 10000)) : 0;
      const color = /^#[0-9A-Fa-f]{3,8}$/.test(data.color || '') ? data.color : '#22c55e';
      const name = (typeof data.name === 'string' ? data.name : 'Support').slice(0, 50);
      setAdminCursor({ x, y, color, name });

      // For AI cursor: show it, then move to target (CSS transition handles animation)
      setAiCursorVisible(true);
      // Use requestAnimationFrame to ensure the cursor is rendered at old position first,
      // THEN update position so CSS transition animates the movement
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAiCursorPos({ x, y });
        });
      });

      // Auto-hide after action completes
      if (cursorHideTimer) clearTimeout(cursorHideTimer);
      cursorHideTimer = setTimeout(() => {
        setAiCursorVisible(false);
      }, 2500);
    });

    // Remote command from admin
    socket.on('command:execute', (data: { command: RemoteCommand }) => {
      executeRemoteCommand(data.command);
    });

    // Session ended
    socket.on('session:ended', () => {
      stopRecording();
      setSession(null);
      setAdminCursor(null);
      setChatMessages([]);
    });

    // Chat message received
    socket.on('chat:receive', (message: ChatMessage) => {
      setChatMessages(prev => [...prev, {
        ...message,
        timestamp: new Date(message.timestamp),
      }]);
    });

    // Admin typing indicator with auto-clear timeout
    let typingTimeout: NodeJS.Timeout | null = null;
    socket.on('chat:typing', (data: { isTyping: boolean; senderType: string }) => {
      if (data.senderType === 'admin') {
        if (typingTimeout) {
          clearTimeout(typingTimeout);
          typingTimeout = null;
        }
        setIsAdminTyping(data.isTyping);
        // Auto-clear typing indicator after 3 seconds if no update
        if (data.isTyping) {
          typingTimeout = setTimeout(() => {
            setIsAdminTyping(false);
          }, 3000);
        }
      }
    });

    // AI Admin requests element resolution — find element in DOM and return coordinates
    // SECURITY: Block queries for sensitive elements (passwords, billing, etc.)
    // Direct click command — bypasses command:send/command:execute round-trip
    // The AI Admin sends this when it wants to click a resolved element directly
    socket.on('element:click', (data: { sessionId: string; query: { selector?: string; textContent?: string } }) => {
      try {
        const searchText = (data.query.textContent || '').toLowerCase().trim();
        if (!searchText) return;

        // Find and click directly — same TreeWalker as element:resolve
        const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
          acceptNode(node) {
            const el = node as Element;
            const tag = el.tagName.toLowerCase();
            if (['script', 'style'].includes(tag)) return NodeFilter.FILTER_REJECT;
            const rect = el.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return NodeFilter.FILTER_SKIP;
            const isInteractive = ['button', 'a', 'input', 'select', 'li', 'label'].includes(tag) ||
              el.getAttribute('role') !== null || el.getAttribute('tabindex') !== null;
            return isInteractive ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
          }
        });

        let best: HTMLElement | null = null;
        let bestScore = 0;
        let n: Node | null;
        while ((n = walker.nextNode())) {
          const el = n as HTMLElement;
          const name = (el.getAttribute('aria-label') || '').toLowerCase();
          const directText = Array.from(el.childNodes)
            .filter(c => c.nodeType === Node.TEXT_NODE || (c.nodeType === Node.ELEMENT_NODE && ['SPAN', 'STRONG', 'EM', 'B'].includes((c as Element).tagName)))
            .map(c => (c.textContent || '').trim()).join(' ').toLowerCase().trim();
          const fullText = (el.textContent || '').trim().toLowerCase();

          if (directText === searchText || name === searchText) { best = el; break; }
          if (fullText === searchText && fullText.length < 50) { if (90 > bestScore) { bestScore = 90; best = el; } }
          if (directText.startsWith(searchText)) { if (80 > bestScore) { bestScore = 80; best = el; } }
        }

        if (best) {
          best.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          setTimeout(() => best!.click(), 100);
        }
      } catch { /* ignore */ }
    });

    socket.on('element:resolve', (data: { sessionId: string; query: { selector?: string; textContent?: string; type?: string } }) => {
      try {
        const BLOCKED_SELECTORS = [
          'input[type="password"]', '[data-stripe]', '[data-billing]',
          '[data-sensitive]', '[data-secret]', '[data-credit-card]',
        ];
        const BLOCKED_TEXT = [
          'delete account', 'deactivate', 'remove agent', 'cancel subscription',
          'change password', 'api key', 'secret', 'billing',
        ];

        // Block sensitive selector queries (regex for stronger matching)
        const selectorLower = (data.query.selector || '').toLowerCase();
        if (/password|credit|secret|billing|stripe|token|ssn|card|api.?key|bank|routing/.test(selectorLower)) {
          socket.emit('element:resolved', { sessionId: data.sessionId, result: { found: false } });
          return;
        }

        let element: Element | null = null;

        // Try selector first (wrapped in try-catch for invalid selectors)
        if (data.query.selector) {
          try {
            element = document.querySelector(data.query.selector);
          } catch {
            // Invalid CSS selector — ignore
          }
        }

        // Smart element matching — accessible names, semantic roles, TreeWalker, priority scoring
        if (!element && data.query.textContent) {
          const searchText = data.query.textContent.toLowerCase().trim();

          // Block queries for sensitive text
          if (BLOCKED_TEXT.some(bt => searchText.includes(bt))) {
            socket.emit('element:resolved', { sessionId: data.sessionId, result: { found: false } });
            return;
          }

          // --- Helper: Compute accessible name (W3C simplified) ---
          const computeAccessibleName = (el: Element): string => {
            // Priority: aria-labelledby > aria-label > direct text > title > placeholder
            const labelledBy = el.getAttribute('aria-labelledby');
            if (labelledBy) {
              return labelledBy.split(' ')
                .map(id => document.getElementById(id)?.textContent?.trim())
                .filter(Boolean).join(' ');
            }
            const ariaLabel = el.getAttribute('aria-label');
            if (ariaLabel) return ariaLabel;
            // Direct child text (not deeply nested)
            const directText = Array.from(el.childNodes)
              .filter(n => n.nodeType === Node.TEXT_NODE ||
                (n.nodeType === Node.ELEMENT_NODE && ['SPAN', 'STRONG', 'EM', 'B', 'I', 'SMALL'].includes((n as Element).tagName)))
              .map(n => (n.textContent || '').trim())
              .join(' ').trim();
            if (directText) return directText;
            return (el as HTMLElement).title || (el as HTMLInputElement).placeholder || el.textContent?.trim() || '';
          };

          // --- Helper: Infer semantic role ---
          const inferRole = (el: Element): string => {
            const explicit = el.getAttribute('role');
            if (explicit) return explicit;
            const tag = el.tagName.toLowerCase();
            const roleMap: Record<string, string> = {
              button: 'button', a: 'link', input: (el as HTMLInputElement).type || 'textbox',
              select: 'combobox', textarea: 'textbox', option: 'option',
              li: 'listitem', label: 'label', th: 'columnheader', td: 'cell',
            };
            return roleMap[tag] || 'generic';
          };

          // --- TreeWalker: exhaustive element discovery ---
          let bestMatch: Element | null = null;
          let bestScore = 0;

          const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
            acceptNode(node) {
              const el = node as Element;
              const tag = el.tagName.toLowerCase();
              // Skip invisible, script, style
              if (tag === 'script' || tag === 'style' || tag === 'noscript') return NodeFilter.FILTER_REJECT;
              const rect = el.getBoundingClientRect();
              if (rect.width === 0 || rect.height === 0) return NodeFilter.FILTER_SKIP;
              // Accept interactive elements
              const role = el.getAttribute('role');
              const isInteractive = ['button', 'a', 'input', 'select', 'textarea', 'option', 'li', 'label'].includes(tag) ||
                ['button', 'tab', 'link', 'option', 'menuitem', 'menuitemradio', 'switch', 'combobox'].includes(role || '') ||
                el.getAttribute('tabindex') !== null ||
                el.getAttribute('onclick') !== null ||
                el.getAttribute('data-value') !== null;
              return isInteractive ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
            }
          });

          let node: Node | null;
          while ((node = walker.nextNode())) {
            const el = node as Element;
            const name = computeAccessibleName(el).toLowerCase();
            const fullText = (el.textContent || '').trim().toLowerCase();
            const role = inferRole(el);

            if (!name && !fullText) continue;
            if (fullText.length > 200) continue;

            let score = 0;

            // Exact match on accessible name (highest)
            if (name === searchText) score = 100;
            // Exact match on full text
            else if (fullText === searchText) score = 90;
            // Name starts with search
            else if (name.startsWith(searchText)) score = 80;
            // Full text starts with search
            else if (fullText.startsWith(searchText)) score = 70;
            // Name contains (short element)
            else if (name.includes(searchText) && name.length < searchText.length * 3) score = 60;
            // Full text contains (short)
            else if (fullText.includes(searchText) && fullText.length < searchText.length * 3) score = 50;
            // Loose contains
            else if (fullText.includes(searchText)) score = 20;

            // Role bonus — prefer buttons/links over generic
            if (role === 'button' || role === 'link') score += 5;
            if (role === 'tab' || role === 'menuitem' || role === 'option') score += 3;

            // State penalty — don't pick hidden/disabled
            if (el.getAttribute('aria-hidden') === 'true') score = 0;
            if ((el as HTMLButtonElement).disabled || el.getAttribute('aria-disabled') === 'true') score -= 10;

            if (score > bestScore) {
              bestScore = score;
              bestMatch = el;
            }
          }

          element = bestMatch;
        }

        // Double-check: don't resolve password/hidden inputs or Stripe elements
        if (element) {
          const tag = element.tagName.toLowerCase();
          const inputType = element.getAttribute('type')?.toLowerCase();
          if (inputType === 'password' || inputType === 'hidden') {
            socket.emit('element:resolved', { sessionId: data.sessionId, result: { found: false } });
            return;
          }
          if (BLOCKED_SELECTORS.some(sel => element!.matches(sel))) {
            socket.emit('element:resolved', { sessionId: data.sessionId, result: { found: false } });
            return;
          }

          const rect = element.getBoundingClientRect();
          const isVisible = rect.width > 0 && rect.height > 0;

          // Store the resolved element AND the query so we can re-find it at click time
          lastResolvedElementRef.current = element as HTMLElement;
          lastResolveQueryRef.current = { selector: data.query.selector, textContent: data.query.textContent };

          socket.emit('element:resolved', {
            sessionId: data.sessionId,
            result: {
              found: isVisible,
              x: Math.round(Math.max(0, Math.min(rect.left + rect.width / 2, window.innerWidth))),
              y: Math.round(Math.max(0, Math.min(rect.top + rect.height / 2, window.innerHeight))),
              width: Math.round(rect.width),
              height: Math.round(rect.height),
              tagName: tag,
              text: (element.textContent || '').trim().slice(0, 50),
              isVisible,
              isDisabled: (element as HTMLButtonElement).disabled || element.getAttribute('aria-disabled') === 'true',
            },
          });
        } else {
          socket.emit('element:resolved', { sessionId: data.sessionId, result: { found: false } });
        }
      } catch {
        socket.emit('element:resolved', { sessionId: data.sessionId, result: { found: false } });
      }
    });

    // Backend requests a fresh full snapshot (when admin joins and no snapshot in buffer)
    // This is debounced on the backend (5s cooldown) to prevent infinite loops in dev mode
    socket.on('snapshot:request', () => {
      // Longer delay to let HMR settle in development mode
      setTimeout(() => {
        if (forceFullSnapshotRef.current && socketRef.current?.connected) {
          forceFullSnapshotRef.current();
        }
      }, 500);
    });

    socketRef.current = socket;
    return socket;
  }, []);

  // Start rrweb recording - lazy loaded
  const startRecording = useCallback(async (sessionId: string, forceCheckout: boolean = false) => {
    // Store session ID in ref so flush interval can access it
    sessionIdRef.current = sessionId;

    // If already recording and forceCheckout is requested, trigger a checkout
    if (stopRecordingRef.current && forceCheckout) {
      // rrweb.record returns a function that when called with true triggers a checkout
      const takeCheckout = stopRecordingRef.current as any;
      if (typeof takeCheckout === 'function') {
        try {
          takeCheckout(true); // This triggers a new full snapshot
        } catch (e) {
          // If checkout fails, restart recording
          stopRecording();
        }
      }
      return;
    }

    // Don't start if already recording
    if (stopRecordingRef.current) return;

    // Lazy load rrweb
    const { rrweb } = await loadDependencies();

    const stopFn = rrweb.record({
      emit: (event) => {
        eventBufferRef.current.push(event);

        // Immediately flush the first batch (which contains the full snapshot)
        // This ensures the admin gets the initial snapshot right away
        if (event.type === 2 && socketRef.current && sessionIdRef.current) {
          socketRef.current.emit('events:batch', {
            sessionId: sessionIdRef.current,
            events: [...eventBufferRef.current],
          });
          eventBufferRef.current = [];
        }
      },
      // Take a new full snapshot every 30 seconds for reliability
      checkoutEveryNms: 30000,
      sampling: {
        // Optimize for performance
        mousemove: true,
        mouseInteraction: true,
        scroll: 150, // Throttle scroll events
        input: 'last', // Only record last input value
      },
      blockClass: 'cobrowse-block', // Block sensitive elements
      maskInputOptions: {
        password: true,
      },
      maskTextClass: 'cobrowse-mask', // Mask sensitive text
      recordCanvas: false, // Skip canvas for performance
      collectFonts: false,
    });

    stopRecordingRef.current = stopFn || null;
    setIsRecording(true);

    // Flush events to server periodically (every 30ms for ultra-low latency)
    flushIntervalRef.current = setInterval(() => {
      if (eventBufferRef.current.length > 0 && sessionIdRef.current && socketRef.current) {
        socketRef.current.emit('events:batch', {
          sessionId: sessionIdRef.current,
          events: eventBufferRef.current,
        });
        eventBufferRef.current = [];
      }
    }, 30);
  }, []);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
      stopRecordingRef.current = null;
    }
    if (flushIntervalRef.current) {
      clearInterval(flushIntervalRef.current);
      flushIntervalRef.current = null;
    }
    eventBufferRef.current = [];
    sessionIdRef.current = null;
    setIsRecording(false);
  }, []);

  // Immediate flush of buffered events (for low-latency response after commands)
  const flushEventsNow = useCallback(() => {
    if (eventBufferRef.current.length > 0 && sessionIdRef.current && socketRef.current) {
      socketRef.current.emit('events:batch', {
        sessionId: sessionIdRef.current,
        events: eventBufferRef.current,
      });
      eventBufferRef.current = [];
    }
  }, []);

  // Execute remote command from admin
  const executeRemoteCommand = useCallback((command: RemoteCommand) => {
    // Shared element resolver used by both `click` and `highlight` handlers.
    // 3-strategy resolve: cached element ref → re-find via selector/textContent → elementsFromPoint fallback.
    const resolveActionTarget = (payload: {
      selector?: string;
      textContent?: string;
      x?: number;
      y?: number;
      cachedElementRef?: any;
    } | undefined | null): HTMLElement | null => {
      // Guard: graceful no-op when payload is missing entirely.
      const safePayload = payload ?? {};
      let target: HTMLElement | null = null;

      // Strategy 1: Use stored element ref (fastest)
      if (lastResolvedElementRef.current && document.body.contains(lastResolvedElementRef.current)) {
        target = lastResolvedElementRef.current;
      }

      // Strategy 2: Re-find using the stored query OR the payload (handles React re-renders)
      if (!target) {
        const querySelector = safePayload.selector ?? lastResolveQueryRef.current?.selector;
        const queryText = safePayload.textContent ?? lastResolveQueryRef.current?.textContent;

        if (querySelector) {
          try { target = document.querySelector(querySelector) as HTMLElement; } catch {}
        }
        if (!target && queryText) {
          const searchText = queryText.toLowerCase().trim();
          const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_ELEMENT, {
            acceptNode(node) {
              const el = node as Element;
              const tag = el.tagName.toLowerCase();
              if (['script', 'style'].includes(tag)) return NodeFilter.FILTER_REJECT;
              const rect = el.getBoundingClientRect();
              if (rect.width === 0 || rect.height === 0) return NodeFilter.FILTER_SKIP;
              const isInteractive = ['button', 'a', 'input', 'select', 'li', 'label'].includes(tag) ||
                el.getAttribute('role') !== null || el.getAttribute('tabindex') !== null;
              return isInteractive ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP;
            }
          });
          let best: HTMLElement | null = null;
          let bestScore = 0;
          let n: Node | null;
          while ((n = walker.nextNode())) {
            const el = n as HTMLElement;
            const text = (el.getAttribute('aria-label') || el.textContent || '').trim().toLowerCase();
            if (text === searchText) { best = el; break; } // Exact match — done
            if (text.startsWith(searchText) && text.length < searchText.length * 2) {
              if (80 > bestScore) { bestScore = 80; best = el; }
            }
          }
          target = best;
        }
      }

      // Strategy 3: elementsFromPoint fallback (skip overlays)
      if (!target && typeof safePayload.x === 'number' && typeof safePayload.y === 'number') {
        const elementsAtPoint = document.elementsFromPoint(safePayload.x, safePayload.y);
        for (const el of elementsAtPoint) {
          const htmlEl = el as HTMLElement;
          const zIndex = parseInt(window.getComputedStyle(htmlEl).zIndex || '0');
          if (zIndex > 9000) continue;
          if (htmlEl.classList.contains('pointer-events-none')) continue;
          if (htmlEl.tagName === 'BODY' || htmlEl.tagName === 'HTML') continue;
          target = htmlEl;
          break;
        }
      }

      return target;
    };

    switch (command.type) {
      case 'click': {
        const clickTarget = resolveActionTarget(command.payload);

        if (clickTarget) {
          // Visual feedback at the element's actual position
          const clickRect = clickTarget.getBoundingClientRect();
          const feedbackX = clickRect.left + clickRect.width / 2;
          const feedbackY = clickRect.top + clickRect.height / 2;
          const clickFeedback = document.createElement('div');
          clickFeedback.style.cssText = `
            position: fixed;
            left: ${feedbackX - 15}px;
            top: ${feedbackY - 15}px;
            width: 30px;
            height: 30px;
            border: 2px solid #3b82f6;
            border-radius: 50%;
            pointer-events: none;
            z-index: 999999;
            animation: cobrowse-pulse 0.5s ease-out forwards;
          `;
          document.body.appendChild(clickFeedback);
          setTimeout(() => clickFeedback.remove(), 500);

          // Scroll into view if needed
          clickTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

          // Execute the click on the ACTUAL element, not coordinates
          clickTarget.click();

          // Concurrent pulsing ring — visual confirmation of what was acted on
          renderInsuraBotHighlight(clickTarget);

          // Clear the ref
          lastResolvedElementRef.current = null;
        }
        break;
      }

      case 'scroll':
        if (command.payload.direction) {
          // AI Agent Mode scroll — direction + amount
          const scrollAmount = command.payload.amount || 300;
          window.scrollBy({
            top: command.payload.direction === 'down' ? scrollAmount : -scrollAmount,
            behavior: 'smooth',
          });
        } else {
          // Human co-browse scroll — absolute x/y
          window.scrollTo({
            top: command.payload.y || 0,
            left: command.payload.x || 0,
            behavior: 'smooth',
          });
        }
        break;

      case 'highlight': {
        // Resolve the actual element (selector/text/cached ref) and wrap its
        // bounding box with a pulsing ring instead of a fixed 50×50 circle.
        const target = resolveActionTarget(command.payload);
        if (!target) break;
        renderInsuraBotHighlight(target);
        break;
      }

      case 'navigate':
        const navPath = command.payload.path || command.payload.url;
        if (navPath && navPath !== 'undefined' && navPath.startsWith('/')) {
          // Use Next.js router for SPA navigation — preserves React state, sockets, and Agent Mode
          // window.location.href would do a full page reload and kill the session
          window.dispatchEvent(new CustomEvent('cobrowse-navigate', { detail: { path: navPath } }));
        }
        break;

      case 'focus':
        const focusElement = document.elementFromPoint(command.payload.x, command.payload.y);
        if (focusElement instanceof HTMLElement) {
          focusElement.focus();
          focusElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        break;
    }

    // Immediately flush any buffered events after command execution
    // Use double requestAnimationFrame to ensure DOM updates are captured
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        flushEventsNow();
      });
    });
  }, [flushEventsNow]);

  // Start co-browse session
  const startSession = useCallback(async () => {
    if (!user?.user?.id) return;

    // Lazy load socket.io and rrweb, then initialize socket
    const socket = await initSocket();

    // Get viewport dimensions for accurate coordinate mapping
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight,
    };

    return new Promise<void>((resolve, reject) => {
      // Hard timeout on the ack: if the server doesn't reply within 15s,
      // surface an error instead of hanging the AI Assist UI forever.
      let settled = false;
      const ackTimeout = setTimeout(() => {
        if (settled) return;
        settled = true;
        reject(new Error('Assistant did not respond. Please try again.'));
      }, 15000);

      socket.emit('session:start', {
        userId: user.user.id,
        userName: user.user.name || 'User',
        userEmail: user.user.email || '',
        currentPath: pathname,
        viewport, // Send viewport to admin for click coordinate scaling
      }, async (response: any) => {
        if (settled) return;
        settled = true;
        clearTimeout(ackTimeout);
        if (response?.success) {
          const sessionId = response.sessionId;
          setSession({
            id: sessionId,
            status: 'waiting',
            remoteControlEnabled: false,
          });
          // Pass sessionId directly to startRecording (now async)
          await startRecording(sessionId);
          resolve();
        } else {
          reject(new Error(response?.error || 'Failed to start session.'));
        }
      });
    });
  }, [user, pathname, initSocket, startRecording]);

  // End co-browse session
  const endSession = useCallback(() => {
    if (session?.id && socketRef.current) {
      socketRef.current.emit('session:end', { sessionId: session.id });
    }
    stopRecording();
    setSession(null);
    setAdminCursor(null);
    setControlRequest(false);
  }, [session?.id, stopRecording]);

  // Grant/deny remote control
  const grantRemoteControl = useCallback((granted: boolean) => {
    if (session?.id && socketRef.current) {
      socketRef.current.emit('control:response', {
        sessionId: session.id,
        granted,
      });
      setSession(prev => prev ? { ...prev, remoteControlEnabled: granted } : null);
    }
  }, [session?.id]);

  // Respond to control request
  const respondToControlRequest = useCallback((granted: boolean) => {
    setControlRequest(false);
    grantRemoteControl(granted);
  }, [grantRemoteControl]);

  // Send chat message
  const sendChatMessage = useCallback((content: string) => {
    if (!session?.id || !socketRef.current || !user?.user?.id) return;

    socketRef.current.emit('chat:send', {
      sessionId: session.id,
      content,
      senderId: user.user.id,
      senderName: user.user.name || 'User',
      senderType: 'user',
    });
  }, [session?.id, user]);

  // Set typing indicator
  const setTyping = useCallback((isTyping: boolean) => {
    if (!session?.id || !socketRef.current || !user?.user?.name) return;

    socketRef.current.emit('chat:typing', {
      sessionId: session.id,
      isTyping,
      senderName: user.user.name,
      senderType: 'user',
    });
  }, [session?.id, user]);

  // Request AI Assist — starts co-browse then tells backend to join as AI admin
  const requestAiAssist = useCallback(async (userMessage?: string) => {
    if (aiStateRef.current !== 'idle') return; // Prevent double-start
    aiStateRef.current = 'starting';

    try {
      await startSession();
      await new Promise(resolve => setTimeout(resolve, 500));

      const sessionId = sessionIdRef.current;
      if (!sessionId) throw new Error('No session established');

      const socket = socketRef.current;
      if (!socket?.connected) throw new Error('Co-browse connection not ready');

      // Role-aware context for the AI (mirrors the old HTTP controller).
      const currentAgency = user?.currentAgency as any;
      const userContext = {
        userType: (user?.user as any)?.userType || 'agent',
        accountType: (user?.user as any)?.accountType || 'basic',
        agencyRole: currentAgency?.role,
        agencyName: currentAgency?.agency?.name || currentAgency?.name,
        hasAgency: !!currentAgency,
        isTrialUser: (user?.user as any)?.isTrialUser || false,
      };

      // Route over the EXISTING websocket — NOT a fresh HTTP request.
      // The websocket is pinned to the backend instance that owns this
      // session, so the AI admin join runs on the right instance. A plain
      // HTTP POST load-balances to a random instance and fails the join.
      const ack: any = await socket
        .timeout(15000)
        .emitWithAck('ai-assist:start', { sessionId, userContext, userMessage });
      if (!ack?.success) {
        throw new Error(ack?.error || 'Failed to start AI assist');
      }

      aiStateRef.current = 'active';
      setAiAssistActive(true);
    } catch (err) {
      aiStateRef.current = 'idle';
      setAiAssistActive(false);
      throw err;
    }
  }, [startSession, user]);

  // Send a user message to the AI — over the same websocket as the start.
  const sendAiAssistMessage = useCallback(async (message: string) => {
    const sessionId = session?.id || sessionIdRef.current;
    const socket = socketRef.current;
    if (!sessionId || !socket?.connected) {
      throw new Error('AI assist connection not ready');
    }
    const ack: any = await socket
      .timeout(15000)
      .emitWithAck('ai-assist:message', { sessionId, message });
    if (!ack?.success) {
      throw new Error(ack?.error || 'Failed to send message');
    }
  }, [session?.id]);

  // Stop AI Assist
  const stopAiAssist = useCallback(async () => {
    if (aiStateRef.current === 'idle' || aiStateRef.current === 'stopping') return;
    aiStateRef.current = 'stopping';

    const sessionId = session?.id || sessionIdRef.current;
    const socket = socketRef.current;
    if (sessionId && socket?.connected) {
      try {
        await socket.timeout(8000).emitWithAck('ai-assist:stop', { sessionId });
      } catch {
        // Best effort
      }
    }

    aiStateRef.current = 'idle';
    setAiAssistActive(false);
    setIsAiAdmin(false);
    endSession();
  }, [session?.id, endSession]);

  // Handle reconnection - rejoin session when socket reconnects
  useEffect(() => {
    if (!session?.id || !socketRef.current || !user?.user?.id) return;

    const socket = socketRef.current;

    const handleReconnect = () => {
      // Re-register with the server to rejoin the session
      socket.emit('session:start', {
        userId: user.user.id,
        userName: user.user.name || 'User',
        userEmail: user.user.email || '',
        currentPath: pathname,
      }, (response: any) => {
        if (response.success) {
          // Update session ID if it changed (shouldn't happen but just in case)
          if (response.sessionId !== session.id) {
            setSession(prev => prev ? { ...prev, id: response.sessionId } : null);
            sessionIdRef.current = response.sessionId;
          }

          // Check if we have a pending snapshot that failed to send
          if (pendingSnapshotRef.current && pendingSnapshotRef.current.length > 0) {
            const eventsToSend = pendingSnapshotRef.current;
            // Clear immediately to prevent infinite retry loop in dev mode
            pendingSnapshotRef.current = null;
            socket.emit('events:batch', {
              sessionId: response.sessionId,
              events: eventsToSend,
            });
          }

          // Force a new full snapshot on reconnect by triggering checkout
          // This ensures the admin gets the current state of the page
          if (stopRecordingRef.current) {
            startRecording(response.sessionId, true); // forceCheckout = true
          } else {
            // Start fresh recording
            startRecording(response.sessionId);
          }
        } else {
          setSession(null);
          setChatMessages([]);
        }
      });
    };

    socket.on('connect', handleReconnect);

    return () => {
      socket.off('connect', handleReconnect);
    };
  }, [session?.id, user, pathname, startRecording]);

  // Force a fresh full snapshot by restarting recording
  const forceFullSnapshot = useCallback(async () => {
    if (!sessionIdRef.current || !socketRef.current || !socketRef.current.connected) {
      return;
    }

    // Stop current recording
    if (stopRecordingRef.current) {
      stopRecordingRef.current();
      stopRecordingRef.current = null;
    }
    if (flushIntervalRef.current) {
      clearInterval(flushIntervalRef.current);
      flushIntervalRef.current = null;
    }
    eventBufferRef.current = [];

    // rrweb should already be loaded if we got here (session is active)
    // but load it just in case
    const { rrweb } = await loadDependencies();

    // Restart recording - this will generate a fresh full snapshot
    const stopFn = rrweb.record({
      emit: (event) => {
        eventBufferRef.current.push(event);

        // Immediately flush full snapshot with acknowledgment
        if (event.type === 2 && socketRef.current && sessionIdRef.current) {
          const eventsToSend = [...eventBufferRef.current];
          eventBufferRef.current = [];

          // Store for retry in case of disconnect
          pendingSnapshotRef.current = eventsToSend;

          socketRef.current.emit('events:batch', {
            sessionId: sessionIdRef.current,
            events: eventsToSend,
          }, (response: any) => {
            // Clear pending snapshot on success
            if (response?.success) {
              pendingSnapshotRef.current = null;
            }
          });
        }
      },
      checkoutEveryNms: 30000,
      sampling: {
        mousemove: true,
        mouseInteraction: true,
        scroll: 150,
        input: 'last',
      },
      blockClass: 'cobrowse-block',
      maskInputOptions: { password: true },
      maskTextClass: 'cobrowse-mask',
      recordCanvas: false,
      collectFonts: false,
    });

    stopRecordingRef.current = stopFn || null;
    setIsRecording(true);

    // Restart flush interval (30ms for ultra-low latency)
    flushIntervalRef.current = setInterval(() => {
      if (eventBufferRef.current.length > 0 && sessionIdRef.current && socketRef.current) {
        socketRef.current.emit('events:batch', {
          sessionId: sessionIdRef.current,
          events: eventBufferRef.current,
        });
        eventBufferRef.current = [];
      }
    }, 30);
  }, []);

  // Keep forceFullSnapshotRef in sync for socket handlers
  useEffect(() => {
    forceFullSnapshotRef.current = forceFullSnapshot;
  }, [forceFullSnapshot]);


  // Track navigation changes
  useEffect(() => {
    if (session?.id && socketRef.current) {
      socketRef.current.emit('user:navigate', {
        sessionId: session.id,
        path: pathname,
      });

      // Force a fresh snapshot after navigation so admin sees the new page
      if (session.status === 'active' && sessionIdRef.current) {
        setTimeout(() => {
          forceFullSnapshot();
        }, 500); // Small delay to let the new page render
      }
    }
  }, [pathname, session?.id, session?.status, forceFullSnapshot]);

  // Track mouse movement for admin to see
  useEffect(() => {
    if (!session || session.status !== 'active') return;

    const handleMouseMove = (e: MouseEvent) => {
      if (socketRef.current && session.id) {
        socketRef.current.emit('cursor:user', {
          sessionId: session.id,
          x: e.clientX,
          y: e.clientY,
        });
      }
    };

    // Throttle mouse events
    let lastEmit = 0;
    const throttledMouseMove = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastEmit > 50) {
        lastEmit = now;
        handleMouseMove(e);
      }
    };

    window.addEventListener('mousemove', throttledMouseMove);
    return () => window.removeEventListener('mousemove', throttledMouseMove);
  }, [session]);

  // Track viewport resize and send updates to admin
  useEffect(() => {
    if (!session || session.status !== 'active' || !socketRef.current) return;

    const handleResize = () => {
      socketRef.current?.emit('user:viewport', {
        sessionId: session.id,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      });
    };

    // Throttle resize events
    let resizeTimeout: NodeJS.Timeout | null = null;
    const throttledResize = () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 200);
    };

    window.addEventListener('resize', throttledResize);
    return () => {
      window.removeEventListener('resize', throttledResize);
      if (resizeTimeout) clearTimeout(resizeTimeout);
    };
  }, [session]);

  // Handle SPA navigation from co-browse commands (preserves React state + Agent Mode)
  useEffect(() => {
    const handleNav = (e: Event) => {
      const path = (e as CustomEvent).detail?.path;
      if (path && path.startsWith('/')) {
        router.push(path);
      }
    };
    window.addEventListener('cobrowse-navigate', handleNav);
    return () => window.removeEventListener('cobrowse-navigate', handleNav);
  }, [router]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [stopRecording]);

  return (
    <CoBrowseContext.Provider
      value={{
        session,
        isRecording,
        startSession,
        endSession,
        grantRemoteControl,
        adminCursor,
        controlRequest,
        respondToControlRequest,
        chatMessages,
        sendChatMessage,
        isAdminTyping,
        setTyping,
        isAiAdmin,
        aiAssistActive,
        requestAiAssist,
        stopAiAssist,
        sendAiAssistMessage,
      }}
    >
      {children}

      {/* AI Agent cursor — always rendered when AI active, moves via CSS transition */}
      {isAiAdmin && session?.status === 'active' && (
        <div
          className="fixed pointer-events-none z-[999999] transition-all duration-500 ease-out"
          style={{
            left: aiCursorPos.x,
            top: aiCursorPos.y,
            transform: 'translate(-50%, -50%)',
            opacity: aiCursorVisible ? 1 : 0,
          }}
        >
          {/* Outer glow ring */}
          <div
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              width: 40,
              height: 40,
              backgroundColor: '#3b82f6',
              opacity: 0.3,
              transform: 'translate(-50%, -50%)',
              animationDuration: '1.5s',
            }}
          />
          {/* Main cursor circle */}
          <div
            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-lg"
            style={{
              backgroundColor: '#3b82f6',
              borderColor: '#ffffff',
              borderWidth: 3,
              boxShadow: '0 0 20px rgba(59,130,246,0.5)',
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"/>
            </svg>
          </div>
          {/* Label */}
          <div
            className="absolute top-10 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs px-2 py-1 rounded-full text-white font-medium shadow-lg"
            style={{ backgroundColor: '#3b82f6' }}
          >
            InsuraBot AI
          </div>
        </div>
      )}

      {/* Human admin cursor overlay (non-AI co-browse) */}
      {adminCursor && !isAiAdmin && (
        <div
          className="fixed pointer-events-none z-[999999] transition-all duration-50"
          style={{
            left: adminCursor.x,
            top: adminCursor.y,
            transform: 'translate(-50%, -50%)',
          }}
        >
          {/* Outer glow ring */}
          <div
            className="absolute inset-0 rounded-full animate-ping"
            style={{
              width: 40,
              height: 40,
              backgroundColor: adminCursor.color || '#22c55e',
              opacity: 0.3,
              transform: 'translate(-50%, -50%)',
            }}
          />
          {/* Main cursor circle */}
          <div
            className="w-8 h-8 rounded-full border-3 flex items-center justify-center text-sm font-bold text-white shadow-lg"
            style={{
              backgroundColor: adminCursor.color || '#22c55e',
              borderColor: '#ffffff',
              borderWidth: 3,
              boxShadow: `0 0 20px ${adminCursor.color || '#22c55e'}80`,
            }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z"/>
            </svg>
          </div>
          {/* Label */}
          <div
            className="absolute top-10 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs px-2 py-1 rounded-full text-white font-medium shadow-lg"
            style={{ backgroundColor: adminCursor.color || '#22c55e' }}
          >
            {isAiAdmin ? 'InsuraBot AI Assisting' : 'Support Agent Controlling'}
          </div>
        </div>
      )}

      {/* AI Agent Mode Frame — clean, premium border when AI is in control */}
      {isAiAdmin && session?.status === 'active' && (
        <div className="fixed inset-0 pointer-events-none z-[999998]">
          {/* Edge border — primary color, subtle but present */}
          <div className="absolute inset-0 border-2 border-primary/15 rounded-sm" />

          {/* Corner accents — animated scanning light along the L-brackets */}
          <svg className="absolute top-2 left-2" width="36" height="36" viewBox="0 0 36 36" fill="none">
            <path d="M2 14V3C2 2.45 2.45 2 3 2H14" className="text-primary/30" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M2 14V3C2 2.45 2.45 2 3 2H14" stroke="url(#scanTL)" strokeWidth="2.5" strokeLinecap="round" />
            <defs><linearGradient id="scanTL" gradientUnits="userSpaceOnUse"><stop stopColor="hsl(var(--primary))" stopOpacity="0"><animate attributeName="offset" values="-0.3;1.3" dur="2.5s" repeatCount="indefinite"/></stop><stop offset="0.15" stopColor="hsl(var(--primary))" stopOpacity="0.8"><animate attributeName="offset" values="-0.15;1.45" dur="2.5s" repeatCount="indefinite"/></stop><stop offset="0.3" stopColor="hsl(var(--primary))" stopOpacity="0"><animate attributeName="offset" values="0;1.6" dur="2.5s" repeatCount="indefinite"/></stop></linearGradient></defs>
          </svg>
          <svg className="absolute top-2 right-2" width="36" height="36" viewBox="0 0 36 36" fill="none">
            <path d="M22 2H33C33.55 2 34 2.45 34 3V14" className="text-primary/30" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M22 2H33C33.55 2 34 2.45 34 3V14" stroke="url(#scanTR)" strokeWidth="2.5" strokeLinecap="round" />
            <defs><linearGradient id="scanTR" gradientUnits="userSpaceOnUse"><stop stopColor="hsl(var(--primary))" stopOpacity="0"><animate attributeName="offset" values="-0.3;1.3" dur="2.5s" repeatCount="indefinite" begin="0.6s"/></stop><stop offset="0.15" stopColor="hsl(var(--primary))" stopOpacity="0.8"><animate attributeName="offset" values="-0.15;1.45" dur="2.5s" repeatCount="indefinite" begin="0.6s"/></stop><stop offset="0.3" stopColor="hsl(var(--primary))" stopOpacity="0"><animate attributeName="offset" values="0;1.6" dur="2.5s" repeatCount="indefinite" begin="0.6s"/></stop></linearGradient></defs>
          </svg>
          <svg className="absolute bottom-2 left-2" width="36" height="36" viewBox="0 0 36 36" fill="none">
            <path d="M2 22V33C2 33.55 2.45 34 3 34H14" className="text-primary/30" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M2 22V33C2 33.55 2.45 34 3 34H14" stroke="url(#scanBL)" strokeWidth="2.5" strokeLinecap="round" />
            <defs><linearGradient id="scanBL" gradientUnits="userSpaceOnUse"><stop stopColor="hsl(var(--primary))" stopOpacity="0"><animate attributeName="offset" values="-0.3;1.3" dur="2.5s" repeatCount="indefinite" begin="1.2s"/></stop><stop offset="0.15" stopColor="hsl(var(--primary))" stopOpacity="0.8"><animate attributeName="offset" values="-0.15;1.45" dur="2.5s" repeatCount="indefinite" begin="1.2s"/></stop><stop offset="0.3" stopColor="hsl(var(--primary))" stopOpacity="0"><animate attributeName="offset" values="0;1.6" dur="2.5s" repeatCount="indefinite" begin="1.2s"/></stop></linearGradient></defs>
          </svg>
          <svg className="absolute bottom-2 right-2" width="36" height="36" viewBox="0 0 36 36" fill="none">
            <path d="M22 34H33C33.55 34 34 33.55 34 33V22" className="text-primary/30" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M22 34H33C33.55 34 34 33.55 34 33V22" stroke="url(#scanBR)" strokeWidth="2.5" strokeLinecap="round" />
            <defs><linearGradient id="scanBR" gradientUnits="userSpaceOnUse"><stop stopColor="hsl(var(--primary))" stopOpacity="0"><animate attributeName="offset" values="-0.3;1.3" dur="2.5s" repeatCount="indefinite" begin="1.8s"/></stop><stop offset="0.15" stopColor="hsl(var(--primary))" stopOpacity="0.8"><animate attributeName="offset" values="-0.15;1.45" dur="2.5s" repeatCount="indefinite" begin="1.8s"/></stop><stop offset="0.3" stopColor="hsl(var(--primary))" stopOpacity="0"><animate attributeName="offset" values="0;1.6" dur="2.5s" repeatCount="indefinite" begin="1.8s"/></stop></linearGradient></defs>
          </svg>

          {/* Top center status pill */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-primary text-white rounded-full pl-3 pr-4 py-1.5 shadow-lg shadow-primary/20">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60" style={{ animationDuration: '2s' }} />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
            </span>
            <span className="text-[11px] font-semibold tracking-wide uppercase">Agent Mode</span>
          </div>

          {/* Bottom status bar — Perplexity-style "Working..." indicator */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2.5 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border border-gray-200/60 dark:border-gray-700/60 rounded-full pl-3.5 pr-5 py-2 shadow-lg">
            <div className="relative w-4 h-4">
              <svg viewBox="0 0 500 410" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-primary overflow-visible">
                <path d="M386.363 22.731H113.636C88.5321 22.731 68.1814 43.0816 68.1814 68.1855V250.004C68.1814 275.108 88.5321 295.458 113.636 295.458H386.363C411.467 295.458 431.818 275.108 431.818 250.004V68.1855C431.818 43.0816 411.467 22.731 386.363 22.731Z" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22.7263 386.371H477.272" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
                <path className="insurabot-eye" d="M204.546 113.636V159.091" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
                <path className="insurabot-eye insurabot-eye-r" d="M295.454 113.636V159.091" stroke="currentColor" strokeWidth="45.4545" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">InsuraBot is working</span>
              <span className="flex gap-0.5">
                <span className="w-1 h-1 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '0ms', animationDuration: '1.4s' }} />
                <span className="w-1 h-1 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '200ms', animationDuration: '1.4s' }} />
                <span className="w-1 h-1 bg-primary/60 rounded-full animate-bounce" style={{ animationDelay: '400ms', animationDuration: '1.4s' }} />
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Highlight animation styles */}
      <style jsx global>{`
        @keyframes cobrowse-pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(2);
            opacity: 0;
          }
        }
      `}</style>
    </CoBrowseContext.Provider>
  );
};

export default CoBrowseProvider;
